// The queue of choices that would still change the schedule.
//
// One card per decision, highest impact first, each one answerable or ignorable.
// The card says what answering it would change rather than which stage of a
// pipeline it came from.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDecisionQueue } from '../../data/decisions.tsx';
import type { Decision } from '../../engine/decisions.ts';
import '../../styles/planning-refinements.css';

const LABEL: Record<Decision['type'], string> = {
  clash: 'Clash',
  preference: 'Preference',
  group: 'Crew',
  rate: 'Artist',
  priority: 'Priority',
};

export function ImprovePlan({ limit = 8, compact = false }: { limit?: number; compact?: boolean }) {
  const { queue, history, ready, summary, answer, dismiss, restore } = useDecisionQueue(limit);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  if (!ready) return <p className="muted" role="status">Loading plan suggestions…</p>;

  const historyPanel = !compact && history.length > 0 && (
    <details className="panel planning-page decision-history">
      <summary>Dismissed and answered ({history.length})</summary>
      <div className="rowlist">
        {history.map((decision) => (
          <div className="row" key={decision.id}>
            <span>
              {decision.title}
              <span className="sub" style={{ display: 'block' }}>
                {decision.status === 'answered' ? 'Answered' : 'Dismissed'} · {decision.context}
              </span>
            </span>
            <button
              type="button"
              className="small ghost"
              aria-label={`Bring back: ${decision.title}`}
              onClick={() => void restore(decision.id)}
            >
              Bring back
            </button>
          </div>
        ))}
      </div>
    </details>
  );

  if (queue.length === 0) {
    return (
      <>
        <div className="panel planning-page">
          <h3>Improve my plan</h3>
          <p className="muted" style={{ margin: 0 }}>
            No suggestions right now. Browse artists or add sets to your plan to keep planning.
          </p>
          {error && <p role="alert" className="planning-error">{error}</p>}
          <div className="planning-actions" style={{ marginTop: 8 }}>
            <button type="button" className="small ghost" onClick={() => navigate('/lineup')}>Browse artists</button>
            {!compact && <button type="button" className="small" onClick={() => navigate('/plan')}>Open my plan</button>}
          </div>
        </div>
        {historyPanel}
      </>
    );
  }

  const shown = compact ? queue.slice(0, 3) : queue;

  return (
    <div className="panel planning-page">
      <div className="panelhead">
        <h3>Improve my plan</h3>
        <span className="roundbadge">{queue.length}</span>
      </div>
      <p className="muted" style={{ margin: '0 0 10px' }}>
        {summary} Choose an option to apply it, or dismiss a suggestion.
      </p>
      {error && <p role="alert" className="planning-error">{error}</p>}
      {busy && <p className="muted" role="status">Saving your choice…</p>}

      <div className="decklist">
        {shown.map((decision) => (
          <article className="deckcard" key={decision.id}>
            <header>
              <span className="kicker">{LABEL[decision.type]}</span>
              <span
                className="impactbar"
                aria-label={`Impact ${Math.round(decision.impact * 100)} out of 100`}
              >
                <i style={{ width: `${Math.max(6, Math.round(decision.impact * 100))}%` }} />
              </span>
            </header>
            <h4>{decision.title}</h4>
            <p className="sub">{decision.context}</p>

            <div className="deckoptions">
              {decision.options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="deckoption"
                  disabled={busy !== ''}
                  onClick={async () => {
                    setBusy(decision.id);
                    setError('');
                    try {
                      const result = await answer(decision, option.id);
                      if (result.goTo) navigate(result.goTo);
                    } catch {
                      setError('That choice could not be saved. Check your plan and try again.');
                    } finally {
                      setBusy('');
                    }
                  }}
                >
                  <strong>{option.label}</strong>
                  {option.sub && <span>{option.sub}</span>}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="small ghost"
              disabled={busy !== ''}
              aria-label={`Dismiss suggestion: ${decision.title}`}
              onClick={async () => {
                setBusy(decision.id);
                setError('');
                try {
                  await dismiss(decision.id);
                } catch {
                  setError('The dismissal could not be saved and may reappear.');
                } finally {
                  setBusy('');
                }
              }}
            >
              Dismiss suggestion
            </button>
          </article>
        ))}
      </div>

      {compact && queue.length > shown.length && (
        <button type="button" className="small" onClick={() => navigate('/plan/improve')}>
          View all {queue.length} suggestions
        </button>
      )}
      {historyPanel}
    </div>
  );
}
